import { StorageProvider } from "../utils/Types";
import { CID } from "multiformats/cid";
import { sha256 } from "multiformats/hashes/sha2";

import { S3Client, DeleteObjectCommand, PutObjectCommand } from "@aws-sdk/client-s3";

import {
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from "@aws-sdk/client-s3";

import { Upload } from "@aws-sdk/lib-storage";
import { ServerError } from "../common/errors";
import axios from "axios";
import Strings from "../config/Strings";

// const STORAGE_API_BASE_URL = "http://127.0.0.1:3011";
// const STORAGE_API_BASE_URL = "https://www.ai-fi.cc";
const STORAGE_API_BASE_URL = "https://plexisign.net";

async function manualUpload(s3Client, bucket, key, file, abortSignal, onProgress) {

  const opts = abortSignal ? {abortSignal} :null
  // 1. 初始化（无 ChecksumAlgorithm）
  const createRes = await s3Client.send(new CreateMultipartUploadCommand({
    Bucket: bucket,
    Key: key,
    // 无 checksum → S3 不强制
  }));
  const uploadId = createRes.UploadId;

  const totalBytes = file.size; // File 或 Blob 才有 .size
  let uploadedBytes = 0; 
  try {
    const partSize = 5 * 1024 * 1024; // 5MB
    const numParts = Math.ceil(file.size / partSize);
    const parts = [];

    // 2. 上传 parts（逐个，无 checksum）
    for (let i = 1; i <= numParts; i++) {
      const start = (i - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const partBody = file.slice(start, end);
      const arrayBuffer = await partBody.arrayBuffer();
      const uploadRes = await s3Client.send(new UploadPartCommand({
        Bucket: bucket,
        Key: key,
        UploadId: uploadId,
        PartNumber: i,
        Body: arrayBuffer,
        // 无 ChecksumAlgorithm 或 ContentMD5 → 纯净上传
      }), opts);
      // 更新进度
      uploadedBytes += partBody.size;
      if (onProgress) {
        onProgress({
          loaded: uploadedBytes,
          total: totalBytes,
          part: i,
          key: key,
          bucket: bucket,
        });
      }

      parts.push({ ETag: uploadRes.ETag, PartNumber: i });
      console.log(`Part ${i} 完成`);
    }

    // 3. 完成（S3 自验完整性）
    const completeRes = await s3Client.send(new CompleteMultipartUploadCommand({
      Bucket: bucket,
      Key: key,
      UploadId: uploadId,
      MultipartUpload: { Parts: parts },
    }));

    return completeRes;
  } catch (error) {
    // 错误时清理
    await s3Client.send(new AbortMultipartUploadCommand({
      Bucket: bucket,
      Key: key,
      UploadId: uploadId,
    }));
    throw error;
  }
}

class S3Storage {
  #stsToken = null;
  #s3Client = null;
  get type() {
    return StorageProvider.S3;
  }

  async axios_get(url, name, progress) {
    // this.cancel();
    this.getAbortController = new AbortController();

    // this.getAbortController = new AbortController();
    try {
      const res = await axios.get(url, {
        signal: this.getAbortController.signal,
        // responseType: "arraybuffer",
        responseType: "blob",
        // maxContentLength: 10000000,
        onDownloadProgress: (event) => {
          if (progress) {
            progress(event);
          }
        },
      });
      if (res.status === 404) {
        throw ServerError.notFoundError(
          Strings.error.client.file_not_found + url
        );
      }
      if (res.status !== 200) {
        throw ServerError.httpError(res);
      }
      this.getAbortController = null;
      const blob = res.data;
      const file = new File([blob], name);
      return file;
    } catch (e) {
      this.getAbortController = null;
      throw e;
    }
  }

  async get(cid, options, name) {
    let key = null;
    let bucket = null;
    let region = 's3';
    if (typeof cid === 'string') {
      key = `users/${window.appConfig.recentII}/${cid}`;
      // bucket = 'pleximailsys';
      bucket = 'plexisignsys';
      region = `s3-us-west-1`
    } else {
      if (!cid.prefix) {
          key = `users/${window.appConfig.recentII}/${cid.cid}`;
      } else {
          key = `${cid.prefix}${cid.cid}`;
      }
      bucket = cid.bucket || 'plexisignsys';
      if (!cid.region) {
          region = `s3-us-west-1`
      } else {
          region = `s3-${cid.region}`;
      }
    }

    // const region = cid.region  ? `s3-${cid.region}` : 's3';
    const url = `https://${bucket}.${region}.amazonaws.com/${key}`;
    // const url = `https://${bucket}.s3.amazonaws.com/${key}`;
    const file = await this.axios_get(url, name, options.progress);
    return file;
  }
  async requestReplyToken(messageUUID) {
    await this.authenticate();
    const clientSecure = await window.plexiMailService.getClientSecure();
    const region = this.#stsToken.s3.region;
    const bucket = this.#stsToken.s3.bucket;
    const resp = await fetch(`${STORAGE_API_BASE_URL}/s3/request-reply-token`, {
      method: "POST",
      body: JSON.stringify({ region: region, bucket: bucket, uid: messageUUID }),
      headers: { 
        "Content-Type": "application/json", 
        'Authorization': 'Bearer ' + clientSecure 
      },
    });

    const res = await resp.json();
    if (!res || !res.data) {
      throw new Error('failed to request reply token');
    }
    return res.data.replyToken;
  }

  saveStsToken(stsToken) {
    const str = JSON.stringify(stsToken);
    window.appConfig.stsToken = str;
  }
  loadStsToken() {
    if (!window.appConfig.stsToken) {
      return null;
    } else {
      return JSON.parse(window.appConfig.stsToken);
    }
  }
  getWallet() {
    return window.wallet.activatedWallet;
  }

  async authenticate() {
    const token = this.#stsToken || this.loadStsToken();

    if (token) {
      const expireAt = new Date(token.credentials.expiration).getTime();
      if (Date.now() <= expireAt - 300000) {
        if (this.#stsToken) {
          return this.#stsToken;
        }
        this.#stsToken = token;

        const s3Client = new S3Client({
          region: this.#stsToken.s3.region,
          credentials: {
            accessKeyId: this.#stsToken.credentials.accessKeyId,
            secretAccessKey: this.#stsToken.credentials.secretAccessKey,
            sessionToken: this.#stsToken.credentials.sessionToken,
          },
        });
        this.#s3Client = s3Client;
        // this.#s3Client.middlewareStack.removeByTag('CHECKSUM');

        return this.#stsToken;
      }
    }

    const clientSecure = await window.plexiMailService.getClientSecureDuringSignup();
    const resp = await fetch(`${STORAGE_API_BASE_URL}/s3/issue-sts`, {
      method: "POST",
      body: JSON.stringify({ userId: window.appConfig.recentII }),
      headers: { 
        "Content-Type": "application/json", 
        'Authorization': 'Bearer ' + clientSecure 
      },
    });

    const res = await resp.json();
    this.saveStsToken(res.data);
    this.#stsToken = res.data;

    const s3Client = new S3Client({
      region: this.#stsToken.s3.region,
      credentials: {
        accessKeyId: this.#stsToken.credentials.accessKeyId,
        secretAccessKey: this.#stsToken.credentials.secretAccessKey,
        sessionToken: this.#stsToken.credentials.sessionToken,
      },
    });
    this.#s3Client = s3Client;
    // this.#s3Client.middlewareStack.removeByTag('CHECKSUM');
    return this.#stsToken;
  }

  async isAuthenticated() {
    const token = this.#stsToken || this.loadStsToken();

    if (token) {
      const expireAt = new Date(token.credentials.expiration).getTime();
      if (Date.now() <= expireAt - 300000) {
        if (this.#stsToken) {
          return true;
        }
      }
    }
    return false;
  }

  async assignStorage() {

    const clientSecure = await window.plexiMailService.getClientSecureDuringSignup();
    const resp = await fetch(`${STORAGE_API_BASE_URL}/s3/assign-storage`, {
      method: "POST",
      body: JSON.stringify({ userId: window.appConfig.recentII }),
      headers: { 
        "Content-Type": "application/json", 
        'Authorization': 'Bearer ' + clientSecure 
      },
    });
    const res = await resp.json();
    if (res.code !== 0) {
      throw new Error(res.msg || "assign storage failed");
    }
  }

  async unloadAgent() {
    this.#stsToken = null;
    this.#s3Client = null;
  }

  async reset() {
    await this.unloadAgent();
    this.saveStsToken("");
  }

  async forceReset() {
    await this.unloadAgent();
    this.saveStsToken("");
  }

  async storageFreeSize() {
    return 100000;
  }

  async hasEnoughSpace(file) {
    return true;
    // const freeSpace = await this.storageFreeSize();
    // return freeSpace - file.size > 102400;
  }
  async getStorageUsage() {
    return { dataLimit: 0, dataUsed: 0 };
  }

  // 辅助函数：将文件读取为 ArrayBuffer
  async readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result); // 读取成功，返回 ArrayBuffer
      reader.onerror = () => reject(new Error("文件读取失败"));
      reader.readAsArrayBuffer(file); // 关键：以二进制方式读取文件
    });
  }

  // 辅助函数：将二进制缓冲区转换为十六进制字符串
  async bufferToHex(buffer) {
    return Array.from(new Uint8Array(buffer))
      .map((byte) => byte.toString(16).padStart(2, "0")) // 每个字节转成两位十六进制
      .join("");
  }
  async putv2(files, options) {
    if (!options) {
      options = {};
    }
    
    await this.authenticate();
    const region = options.region ? options.region : (this.#stsToken ? this.#stsToken.s3.region : null);

    const code = 0x55;
    const version = 1;
    const result = {};
    const prefix = options.prefix || this.#stsToken.s3.prefix;
    const bucket = options.bucket || this.#stsToken.s3.bucket;

    for (const file of files) {
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const hashArrayBuffer = await crypto.subtle.digest(
        "SHA-256",
        arrayBuffer
      );
      const hashBuffer = new Uint8Array(hashArrayBuffer);
      const digest = await sha256.digest(hashBuffer);
      const cidObj = CID.create(version, code, digest);
      const cid = cidObj.toString();
      const key = `${prefix}${cid}`;
      if (options && options.onRootCidReady) {
        options.onRootCidReady({bucket, prefix, region, cid});
      }

      const command = new PutObjectCommand({
          Bucket: bucket, 
          Key: key, 
          Body: file,
          ContentType: file.type,
          Metadata: {
              oname: file.name
          }
      });


      const res =  await this.#s3Client.send(command);
      return {
          name: file.name,
          region: region,
          bucket: bucket,
          prefix: prefix,
          cid: cid,
          key: key,
          res,
        };

// const command = new PutObjectCommand({
//     Bucket: bucket,
//     Key: key,
//     Body: fileContent,
//     ContentType: "application/octet-stream", // Set appropriate MIME type
//   });

//   try {
//     const response = await s3Client.send(command);
//     console.log("Upload successful:", response.ETag);
//     return response;
//   } catch (error) {
//     console.error("Upload failed:", error);
//     throw error;
//   }
    }
    return result;
  }
  


  async put(files, options) {
    if (!options) {
      options = {};
    }
    
    await this.authenticate();
    const region = options.region ? options.region : (this.#stsToken ? this.#stsToken.s3.region : null);

    const code = 0x55;
    const version = 1;
    const result = {};
    const prefix = options.prefix || this.#stsToken.s3.prefix;
    const bucket = options.bucket || this.#stsToken.s3.bucket;

    for (const file of files) {
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const hashArrayBuffer = await crypto.subtle.digest(
        "SHA-256",
        arrayBuffer
      );
      const hashBuffer = new Uint8Array(hashArrayBuffer);
      const digest = await sha256.digest(hashBuffer);
      const cidObj = CID.create(version, code, digest);
      const cid = cidObj.toString();
      const key = `${prefix}${cid}`;
      if (options && options.onRootCidReady) {
        options.onRootCidReady({bucket, prefix, region, cid});
      }
      // const command = PutObjectCommand({
      //     Bucket: options.bucket,
      //     Key: `${options.prefix}${cid}`,
      //     Body: file,
      //     ContentType: file.type,
      //     Metadata: {
      //         id: "123", // 会自动转为 x-amz-meta-id
      //         owner: "user1" // 会自动转为 x-amz-meta-owner
      //     }
      // });

      // const res =  await this.#s3Client.send(command);
      // result[cid] = {name: file.name, cid: cid, key: key, res};
      const res = await manualUpload(this.#s3Client, bucket, key, file, options.signal, (progress) => {
        if (options && options.onUploadProgress) {
          options.onUploadProgress(progress);
        }
      });
      
      result[cid] = {
        name: file.name,
        region: region,
        bucket: bucket,
        prefix: prefix,
        cid: cid,
        key: key,
        res,
      };
      if (files && files.length === 1) {
        return {
          name: file.name,
          region: region,
          bucket: bucket,
          prefix: prefix,
          cid: cid,
          key: key,
          res,
        }
      }
    }
    return result;
  }

  async putv3(files, options) {
    if (!options) {
      options = {};
    }
    
    await this.authenticate();
    const region = options.region ? options.region : (this.#stsToken ? this.#stsToken.s3.region : null);

    const code = 0x55;
    const version = 1;
    const result = {};
    const prefix = options.prefix || this.#stsToken.s3.prefix;
    const bucket = options.bucket || this.#stsToken.s3.bucket;

    for (const file of files) {
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const hashArrayBuffer = await crypto.subtle.digest(
        "SHA-256",
        arrayBuffer
      );
      const hashBuffer = new Uint8Array(hashArrayBuffer);
      const digest = await sha256.digest(hashBuffer);
      const cidObj = CID.create(version, code, digest);
      const cid = cidObj.toString();
      const key = `${prefix}${cid}`;
      if (options && options.onRootCidReady) {
        options.onRootCidReady({bucket, prefix, region, cid});
      }
      // const command = PutObjectCommand({
      //     Bucket: options.bucket,
      //     Key: `${options.prefix}${cid}`,
      //     Body: file,
      //     ContentType: file.type,
      //     Metadata: {
      //         id: "123", // 会自动转为 x-amz-meta-id
      //         owner: "user1" // 会自动转为 x-amz-meta-owner
      //     }
      // });

      // const res =  await this.#s3Client.send(command);
      // result[cid] = {name: file.name, cid: cid, key: key, res};

      const parallelUploads3 = new Upload({
        client: this.#s3Client,
        params: { 
          Bucket: bucket, 
          Key: key, 
          Body: file,
          ContentType: file.type,
          // ChecksumAlgorithm: "NONE",
        },
        disableMultipartChecksum: true,
        // partSize: Infinity,
        // // optional tags
        // tags: [
        // ],
        // // additional optional fields show default values below:
        // // (optional) concurrency configuration
        // queueSize: 4,
        // // (optional) size of each part, in bytes, at least 5MB
        // partSize: 1024 * 1024 * 5,
        // // (optional) when true, do not automatically call AbortMultipartUpload when
        // // a multipart upload fails to complete. You should then manually handle
        // // the leftover parts.
        // leavePartsOnError: false,
      });

      parallelUploads3.on("httpUploadProgress", (progress) => {
        console.log(progress);
        if (options.onUploadProgress) {
          options.onUploadProgress(progress);
        }
      });

      const res = await parallelUploads3.done();
      result[cid] = {
        name: file.name,
        region: region,
        bucket: bucket,
        prefix: prefix,
        cid: cid,
        key: key,
        res,
      };
      if (files && files.length === 1) {
        return {
          name: file.name,
          region: region,
          bucket: bucket,
          prefix: prefix,
          cid: cid,
          key: key,
          res,
        }
      }
    }
    return result;
  }

  async remove(cid, options) {
    await this.authenticate();

    // var key = null;
    // var bucket = null;
    // if (typeof cid === "string") {
    //   key = `users/${window.appConfig.recentII}/`;
    //   bucket = "pleximailsys";
    // } else {
    //   key = `${cid.prefix}${cid.cid}`;
    //   bucket = cid.bucket;
    // }


    let key = null;
    let bucket = null;
    // let region = 's3';
    if (typeof cid === 'string') {
      key = `users/${window.appConfig.recentII}/${cid}`;
      // bucket = 'pleximailsys';
      bucket = 'plexisignsys';
      // region = `s3-us-west-1`
    } else {
      if (!cid.prefix) {
          key = `users/${window.appConfig.recentII}/${cid.cid}`;
      } else {
          key = `${cid.prefix}${cid.cid}`;
      }
      bucket = cid.bucket || 'plexisignsys';
      // if (!cid.region) {
      //     region = `s3-us-west-1`
      // } else {
      //     region = `s3-${cid.region}`;
      // }
    }

    const command = new DeleteObjectCommand({
      Bucket: bucket,
      Key: key,
    });

    const res = await this.#s3Client.send(command);

    return res;
  }
}

export default S3Storage;
